/**
 * The catalogue of pop-up announcements.
 *
 * An announcement is CODE, not content: each slug below is implemented as a
 * React component in `apps/webapp/src/features/announcements/`, built from a
 * Figma mockup. Nothing about how it looks or what it says lives in the
 * database, which is what lets a designer and Claude Code iterate on one
 * without a migration or an editor.
 *
 * This file is the one list all three apps agree on:
 *
 *   api     validates a scheduled row's slug, and builds the deep link a push
 *           notification points at
 *   webapp  maps each slug to its component, exhaustively — a slug added here
 *           without a component is a type error
 *   admin   lists them in the visualizer, and schedules one into a room
 *
 * `presentation` is how the announcement occupies the screen. It lives here
 * rather than on the component because the admin visualizer has to frame the
 * preview before it has rendered anything.
 */
export const ANNOUNCEMENT_PRESENTATIONS = ["sheet", "modal"] as const;

export type AnnouncementPresentation =
  (typeof ANNOUNCEMENT_PRESENTATIONS)[number];

/**
 * Who a GLOBAL schedule of an announcement reaches.
 *
 * A schedule row names a room or no room. A room row reaches the room: its
 * members joined for what the room shows them, so the room is the audience. A
 * global row reaches every reader the announcement's audience admits, and that
 * audience is a rule written in code (`apps/api/src/domain/announcement-audience.ts`),
 * never a condition assembled in the admin. The admin shows each rule's label
 * and description, and how many readers it admits today; changing who an
 * announcement reaches is a code change, reviewed like the announcement itself.
 *
 * The rules run on the server only. The webapp keeps receiving slugs.
 */
export const ANNOUNCEMENT_AUDIENCES = ["everyone", "oncologists"] as const;

export type AnnouncementAudience = (typeof ANNOUNCEMENT_AUDIENCES)[number];

type AnnouncementAudienceDefinition = {
  /** What the admin calls it. */
  label: string;
  /** The rule in words. The test beside the rule keeps the two in step. */
  description: string;
};

export const ANNOUNCEMENT_AUDIENCE_DEFINITIONS = {
  everyone: { label: "Everyone", description: "Every signed-in reader." },
  oncologists: {
    label: "Oncologists",
    description:
      "Readers whose main specialty, or one of their specialties, is oncology, radiotherapy oncology, hematologic oncology, neurologic oncology or genitourinary oncology, whatever rooms they belong to.",
  },
} as const satisfies Record<
  AnnouncementAudience,
  AnnouncementAudienceDefinition
>;

type AnnouncementDefinition = {
  /** What the admin visualizer and the scheduling screen call it. */
  title: string;
  presentation: AnnouncementPresentation;
  /** Who a global schedule of it reaches. A room schedule reaches its room. */
  audience: AnnouncementAudience;
};

/**
 * The slugs, listed before the definitions so the type is a real union and the
 * `satisfies` below can demand that every one of them is implemented. Deriving
 * the list from `Object.keys` instead would need a cast, and would not.
 */
export const ANNOUNCEMENT_SLUGS = [
  "juisci-gpt",
  "feed",
  "healthy-hour-braf",
] as const;

export type AnnouncementSlug = (typeof ANNOUNCEMENT_SLUGS)[number];

export const ANNOUNCEMENTS = {
  "juisci-gpt": {
    title: "Juisci GPT: answers you can trace",
    presentation: "sheet",
    audience: "everyone",
  },
  feed: {
    title: "Feed: follow the topics that matter",
    presentation: "sheet",
    audience: "everyone",
  },
  "healthy-hour-braf": {
    title: "Healthy Hour: BRAF V600E mCRC webinar, Sep 22",
    presentation: "sheet",
    audience: "oncologists",
  },
} as const satisfies Record<AnnouncementSlug, AnnouncementDefinition>;

/**
 * The query parameter that opens an announcement on a discovery surface.
 *
 * One name, shared by the three things that have to agree on it: the push
 * notification's deep link, the webapp that reads it, and the admin visualizer,
 * whose preview IS that URL in an iframe rather than a second rendering path.
 */
export const ANNOUNCEMENT_QUERY_PARAM = "announcement";

/**
 * The slug a string names, or null. Returns the value rather than asserting
 * about it: a type predicate would let the compiler trust a claim the function
 * makes, where `.find` over the literal tuple narrows on evidence.
 */
export function toAnnouncementSlug(value: string): AnnouncementSlug | null {
  const found = ANNOUNCEMENT_SLUGS.find((slug) => slug === value);
  return found === undefined ? null : found;
}

/** The deep link that opens an announcement on the public discovery feed. */
export function announcementDeepLink(
  appUrl: string,
  slug: AnnouncementSlug,
): string {
  return `${appUrl}/discovery?${ANNOUNCEMENT_QUERY_PARAM}=${slug}`;
}

/**
 * The knobs the admin visualizer offers when previewing an announcement.
 *
 * Declared here, served by the registry endpoint, and rendered as a form the
 * admin does not hand-write: the same shape the email-template gallery already
 * uses for its props. An announcement is code, so the day one of them takes a
 * variable (a room name, a count, a partner) it is declared here with a default
 * and the form grows a field on its own.
 *
 * Today there is exactly one, and every announcement has it. An announcement
 * says the same thing to everyone; only the LANGUAGE it says it in differs.
 */
export const ANNOUNCEMENT_PARAM_KINDS = ["select"] as const;

export type AnnouncementParamKind = (typeof ANNOUNCEMENT_PARAM_KINDS)[number];

export type AnnouncementParam = {
  key: string;
  label: string;
  kind: AnnouncementParamKind;
  /** What the preview uses before the form is touched. */
  defaultValue: string;
  options: ReadonlyArray<{ value: string; label: string }>;
};

export const ANNOUNCEMENT_LANGUAGE_PARAM = "lang";

/**
 * The preview URL, which is NOT the reader's app: no feed behind it, no
 * session, no dismissal recorded, and the language is whatever the form asks
 * for. The admin frames this. A push notification points at
 * `announcementDeepLink` instead, which is the real thing.
 */
export function announcementPreviewUrl(
  appUrl: string,
  slug: AnnouncementSlug,
  params: Readonly<Record<string, string>>,
): string {
  const query = new URLSearchParams(params).toString();
  const base = `${appUrl}/announcements/preview/${slug}`;
  return query === "" ? base : `${base}?${query}`;
}
